/**
 * useRoomPrep (PPL-30) — solo-prep chat hook scoped to a single mediation.
 *
 * Why a dedicated hook (and not `useF8Chat` re-pointed): `useF8Chat` is bound
 * to the per-user `users/{uid}/chatThreads/{threadId}/messages` collection —
 * thread creation, deletion, message writes via `chatEurW`, mentions, etc.
 * The mediation prep thread lives at `mediations/{id}/prep/{uid}/messages`
 * and writes go through `sendRoomPrepMessage` (the consent chokepoint)
 * rather than `chatEurW`. Re-pointing the existing hook would mean grafting
 * a parallel write path onto a hook whose threads model has no analogue here
 * (there's exactly ONE prep thread per (mediation, user)); a focused hook is
 * cleaner and keeps the consent invariant explicit.
 *
 * What it owns:
 *  - Realtime subscription to the caller's OWN prep thread (never the other
 *    side's).
 *  - Idempotent `startRoomPrep` call on mount to seed the thread.
 *  - Realtime subscription to the root mediation doc so the page can read
 *    which side the caller is on and surface state changes (ended_solo, etc.).
 *  - Send-message wrapping with an in-flight `isSending` flag for the typing
 *    indicator.
 *  - End-prep helper.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  subscribeToRoom,
  subscribeToPrepMessages,
  startRoomPrep,
  sendRoomPrepMessage,
  endRoomPrepSolo,
} from '../services/roomService';
import type { SubscriptionMeta } from '../services/roomService';
import type { Room, RoomMessage } from '../types/room';

/** Caller side in a mediation, derived from inviter/invitee uids. */
export type RoomSide = 'inviter' | 'invitee' | null;

export interface UseRoomPrepResult {
  /** Root mediation doc (state, consent, ready, side, …). Null until loaded. */
  mediation: Room | null;
  /** Chronological prep messages for THIS caller only. */
  messages: RoomMessage[];
  /** Which side the caller is on — drives sample-question chips. */
  side: RoomSide;
  /** True while a `sendMessage` callable is in flight. */
  isSending: boolean;
  /** True while `endPrep` is in flight. */
  isEnding: boolean;
  /** True after the first onSnapshot batch (or first error). */
  initialLoadComplete: boolean;
  /** Latest error message from any underlying call, or null. */
  error: string | null;
  /**
   * True when the prep message subscription has hit the 100-message cap.
   * Consumers should render an archived-messages notice.
   */
  atLimit: boolean;
  /** Send a user message; throws on failure (the caller handles UX). */
  sendMessage: (text: string) => Promise<void>;
  /** End the prep thread — first-class exit. */
  endPrep: () => Promise<void>;
}

export default function useRoomPrep(
  mediationId: string | null | undefined,
  uid: string | null | undefined,
): UseRoomPrepResult {
  const [mediation, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<RoomMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atLimit, setAtLimit] = useState(false);

  // Idempotent server-side "open" — safe to retry; if the thread already
  // exists, the callable no-ops. Best-effort: we never block the subscriber
  // on it (the subscriber works against an existing thread either way).
  //
  // Guarded with a ref (not state) keyed by mediationId: a StrictMode
  // double-mount fires this effect twice before any state update lands, so a
  // state flag can't stop the second call — that double-invoke could seed two
  // f8 openers (PPL-154). The ref is set synchronously, so the second run bails.
  // (The server also guards this atomically via claimPrepOpener — defence in
  // depth.) Keyed by mediationId so switching rooms still re-opens.
  const openedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mediationId || openedForRef.current === mediationId) return;
    openedForRef.current = mediationId;
    (async () => {
      try {
        await startRoomPrep(mediationId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('startRoomPrep failed:', err);
        // Clear the guard so a remount / room switch can retry (the effect deps
        // are [mediationId], so this won't itself re-run within the current mount).
        if (openedForRef.current === mediationId) openedForRef.current = null;
      }
    })();
  }, [mediationId]);

  // Root doc subscriber — gives us inviter/invitee uids so we can compute
  // `side`, and surfaces consent/ready/state changes the page may want.
  useEffect(() => {
    if (!mediationId) return undefined;
    const unsub = subscribeToRoom(
      mediationId,
      (data) => setRoom(data),
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('subscribeToRoom error:', err);
        setError(err?.message ?? 'failed to load mediation');
      },
    );
    return unsub;
  }, [mediationId]);

  // Prep-message subscriber — caller's own prep ONLY.
  useEffect(() => {
    if (!mediationId || !uid) return undefined;
    const unsub = subscribeToPrepMessages(
      mediationId,
      uid,
      (msgs) => {
        setMessages(msgs);
        setInitialLoadComplete(true);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('subscribeToPrepMessages error:', err);
        setError(err?.message ?? 'failed to load prep messages');
        setInitialLoadComplete(true);
      },
      (meta: SubscriptionMeta) => setAtLimit(meta.atLimit),
    );
    return unsub;
  }, [mediationId, uid]);

  // Derive which side the caller is on. Mirrors the inviter/invitee logic the
  // server uses to pick a prep-coach calibration in `buildPrepCoachPrompt`.
  const side: RoomSide = useMemo(() => {
    if (!mediation || !uid) return null;
    if (mediation.inviterUid === uid) return 'inviter';
    if (mediation.inviteeUid === uid) return 'invitee';
    return null;
  }, [mediation, uid]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !mediationId || isSending) return;
      setIsSending(true);
      try {
        await sendRoomPrepMessage(mediationId, trimmed);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'failed to send';
        setError(msg);
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [mediationId, isSending],
  );

  const endPrep = useCallback(async () => {
    if (!mediationId || isEnding) return;
    setIsEnding(true);
    try {
      await endRoomPrepSolo(mediationId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'failed to end prep';
      setError(msg);
      throw err;
    } finally {
      setIsEnding(false);
    }
  }, [mediationId, isEnding]);

  return {
    mediation,
    messages,
    side,
    isSending,
    isEnding,
    initialLoadComplete,
    error,
    atLimit,
    sendMessage,
    endPrep,
  };
}
